// src/components/Inventory/charts/TrendLineChart.jsx
import React, { memo, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  Brush,
  ReferenceLine,
} from "recharts";
import ChartCard from "../ChartCard";
import { chartPalette } from "./chartPalette";
import { getTooltipProps } from "./tooltipProps";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import {
  isYearBoundary,
  isQuarterBoundary,
  getYearFromPeriod,
  getQuarterLabel,
} from "../../../utils/inventory/periodUtils";

function TrendLineChart({
  data,
  darkMode,
  title,
  subtitle,
  height = 360,
  chartKey,
  unit = "lbs",
  series = [],
  period = "D",
  t,
  currency = "CAD",
}) {
  const axisColor = darkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";
  const gridColor = darkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";

  const [hidden, setHidden] = useState({});

  useEffect(() => {
    setHidden({});
  }, [chartKey]);

  const handleLegendClick = (o) => {
    const key = o?.dataKey;
    if (!key) return;
    setHidden((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const usingMulti = Array.isArray(series) && series.length > 0;

  const boundaries = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const out = [];

    for (const row of rows) {
      const p = row?.period;
      if (!p) continue;

      const yearLine = isYearBoundary(p, period);
      const quarterLine = !yearLine && isQuarterBoundary(p, period);
      if (!yearLine && !quarterLine) continue;

      const year = getYearFromPeriod(p);
      const q = getQuarterLabel(p, period);

      out.push({ period: p, yearLine, quarterLine, year, q });
    }

    return out;
  }, [data, period]);

  const totalLabel = t ? t("Total") : "Total";
  const unitLabel = t ? t(unit, unit) : unit;
  const pointCount = Array.isArray(data) ? data.length : 0;

  return (
    <ChartCard
      icon={faChartLine}
      iconColor={unit === "$" ? "amber" : "green"}
      title={title}
      badge={pointCount > 0 ? `${pointCount} ${t ? t("points") : "points"}` : null}
    >
      <Box className="inv-chart-wrap" sx={{ height, mt: 1 }}>
        <ResponsiveContainer key={chartKey} width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 18, left: 6, bottom: 10 }}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />

            {boundaries.map((b) => (
              <ReferenceLine
                key={`ref-${b.period}`}
                x={b.period}
                stroke={darkMode ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)"}
                strokeWidth={b.yearLine ? 2 : 1}
                strokeDasharray={b.yearLine ? "0" : "4 6"}
                label={
                  b.yearLine
                    ? {
                        position: "insideTopLeft",
                        value: b.year,
                        fill: darkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
                        fontSize: 12,
                        fontWeight: 900,
                      }
                    : b.quarterLine
                    ? {
                        position: "insideTopLeft",
                        value: `${b.year} ${b.q}`,
                        fill: darkMode ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.38)",
                        fontSize: 11,
                        fontWeight: 800,
                      }
                    : undefined
                }
              />
            ))}

            <XAxis
              dataKey="period"
              tick={{ fontSize: 12, fill: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickLine={{ stroke: axisColor }}
              interval="preserveStartEnd"
              minTickGap={18}
            />

            <YAxis
              tick={{ fontSize: 12, fill: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickLine={{ stroke: axisColor }}
              allowDecimals={false}
            />

            <RTooltip
              {...getTooltipProps(darkMode)}
              formatter={(v, name) => {
                const num = Number(v);
                if (unit === "$") {
                  return [
                    num.toLocaleString(undefined, { style: "currency", currency }),
                    name,
                  ];
                }
                return [`${num.toLocaleString()} ${unitLabel}`, name];
              }}
              labelFormatter={(label) => `${label}`}
            />

            <Legend
              verticalAlign="top"
              align="right"
              height={32}
              onClick={handleLegendClick}
              wrapperStyle={{
                cursor: "pointer",
                color: darkMode ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.7)",
                fontSize: 12,
                fontWeight: 700,
                paddingBottom: 6,
              }}
            />

            {!usingMulti ? (
              <Line
                type="monotone"
                dataKey="value"
                name={totalLabel} // ✅ translated
                hide={Boolean(hidden.value)}
                stroke={
                  unit === "$"
                    ? darkMode
                      ? "rgba(253,221,96,0.95)"
                      : "rgba(181,135,0,0.95)"
                    : darkMode
                    ? "rgba(124,255,178,0.95)"
                    : "rgba(30,130,90,0.95)"
                }
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
            ) : (
              series.map((s, idx) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  hide={Boolean(hidden[s.key])}
                  stroke={chartPalette[idx % chartPalette.length]}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))
            )}

            <Brush
              dataKey="period"
              height={28}
              travellerWidth={10}
              stroke={darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"}
              fill={darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );
}

export default memo(TrendLineChart);
